"use client";
import { logout } from "@/app/(my-app)/auth/actions";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toKebabCase } from "@/lib/utils";
import { Professional_User, User } from "@/types/types";
import {
  Baby,
  Book,
  Calculator,
  Calendar,
  ChevronDown,
  Cookie,
  Droplets,
  Drumstick,
  Dumbbell,
  FileText,
  FolderOpenDot,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Ruler,
  Scale,
  Thermometer,
  User as UserIcon,
  Users,
  Utensils,
  Weight,
} from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { MobileNav } from "./MobileNav";
const ThemeChanger = dynamic(() => import("./ThemeChanger"), { ssr: false });
const ClientImage = dynamic(() => import("./ClientImage"), { ssr: false });

export function Header({
  user,
}: {
  user: (User & { professional_users: Professional_User }) | null;
}) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const linkToUser = toKebabCase(
    user?.professional_users?.full_name || user?.full_name || ""
  );
  const isProfesional = user?.role === "profesional";

  return (
    <header className="sticky z-50 top-0 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4 lg:px-6">
        <div className="flex items-center">
          <Link className="flex items-center justify-center" href="/">
            <ClientImage
              srcLight="/logos/png/light/horizontal-light-2.png"
              srcDark="/logos/png/dark/horizontal-dark-2.png"
              alt="Nutriolift logo"
              width={196}
              height={196}
              priority
              loading="eager"
            />
          </Link>
        </div>

        <div className="flex items-center space-x-2">
          <ThemeChanger />

          <nav className="hidden md:flex items-center space-x-2">
            {/* Enlaces comunes para todos - Blog y Recetas */}
            <Link href="/blog">
              <Button variant="permanentLink" className="transition-colors">
                <Book className="mr-2 h-4 w-4" />
                Blog
              </Button>
            </Link>
            <Link href="/recetas">
              <Button variant="permanentLink" className="transition-colors">
                <Cookie className="mr-2 h-4 w-4" />
                Recetas
              </Button>
            </Link>

            {/* Menú de calculadoras - para todos */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="permanentLink"
                  className="w-full justify-between transition-colors"
                >
                  <div className="flex items-center">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculadoras
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {/* Enlace a todas las calculadoras */}
                <DropdownMenuItem asChild>
                  <Link
                    href="/calculadora"
                    className="flex items-center font-medium"
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    <span>Todas las calculadoras</span>
                  </Link>
                </DropdownMenuItem>

                {/* Calculadoras básicas */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Calculadoras Básicas</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/calorias"
                      className="flex items-center"
                    >
                      <Calculator className="mr-2 h-4 w-4" />
                      <span>Calculadora de calorías</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/indice-masa-corporal"
                      className="flex items-center"
                    >
                      <Ruler className="mr-2 h-4 w-4" />
                      <span>Calculadora de IMC</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/peso-ideal"
                      className="flex items-center"
                    >
                      <Weight className="mr-2 h-4 w-4" />
                      <span>Peso ideal</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                {/* Calculadoras nutricionales */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Nutricionales</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/equivalencia-alimentos-crudos-cocidos"
                      className="flex items-center"
                    >
                      <Utensils className="mr-2 h-4 w-4" />
                      <span>Equivalencia de alimentos</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/macros"
                      className="flex items-center"
                    >
                      <Calculator className="mr-2 h-4 w-4" />
                      <span>Calculadora de macros</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/proteinas"
                      className="flex items-center"
                    >
                      <Drumstick className="mr-2 h-4 w-4" />
                      <span>Calculadora de proteínas</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/agua"
                      className="flex items-center"
                    >
                      <Droplets className="mr-2 h-4 w-4" />
                      <span>Calculadora de agua</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                {/* Composición Corporal */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Composición Corporal</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/tasa-metabolica-basal"
                      className="flex items-center"
                    >
                      <Thermometer className="mr-2 h-4 w-4" />
                      <span>Tasa metabólica basal</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/porcentaje-grasa"
                      className="flex items-center"
                    >
                      <Scale className="mr-2 h-4 w-4" />
                      <span>Porcentaje de grasa</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                {/* Calculadoras especiales */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Especiales</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/calorias-ejercicio"
                      className="flex items-center"
                    >
                      <Dumbbell className="mr-2 h-4 w-4" />
                      <span>Calorías en ejercicio</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/calculadora/peso-embarazo"
                      className="flex items-center"
                    >
                      <Baby className="mr-2 h-4 w-4" />
                      <span>Peso de embarazo</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Contenido para usuarios autenticados */}
            {user ? (
              <>
                {/* Ver perfil */}
                <Link
                  href={
                    isProfesional
                      ? `/nutricionista-dietista/${linkToUser}`
                      : "/perfil"
                  }
                >
                  <Button variant="permanentLink" className="transition-colors">
                    <UserIcon className="mr-2 h-4 w-4" />
                    Ver perfil
                  </Button>
                </Link>

                {/* Mi cuenta */}
                <Link href="/mi-cuenta">
                  <Button variant="permanentLink" className="transition-colors">
                    <UserIcon className="mr-2 h-4 w-4" />
                    Mi cuenta
                  </Button>
                </Link>

                {/* Panel profesional - solo para profesionales */}
                {isProfesional && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="permanentLink"
                        className="w-full justify-between transition-colors"
                      >
                        <div className="flex items-center">
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          Panel Profesional
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      <DropdownMenuItem asChild>
                        <Link href="/dashboard" className="flex items-center">
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          <span>Panel de Control</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href="/dashboard/formularios"
                          className="flex items-center"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          <span>Formularios</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href="/dashboard/clientes"
                          className="flex items-center"
                        >
                          <Users className="mr-2 h-4 w-4" />
                          <span>Clientes</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href="/dashboard/dietas"
                          className="flex items-center"
                        >
                          <FolderOpenDot className="mr-2 h-4 w-4" />
                          <span>Dietas</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href="/dashboard/calendario"
                          className="flex items-center"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          <span>Calendario</span>
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Cerrar sesión */}
                <form action={logout} className="inline">
                  <Button
                    type="submit"
                    variant="permanentLink"
                    className="transition-colors"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar sesión
                  </Button>
                  <input type="hidden" name="redirectTo" value={pathname} />
                </form>
              </>
            ) : (
              <>
                {/* Enlaces solo para usuarios no autenticados */}
                <Link href="/registro">
                  <Button variant="permanentLink" className="transition-colors">
                    <UserIcon className="mr-2 h-4 w-4" />
                    Soy Nutricionista o Dietista
                  </Button>
                </Link>
                <Link
                  href={`/login${
                    pathname !== "/" && pathname !== "/registro"
                      ? "?redirectTo=" + pathname
                      : "/"
                  }`}
                >
                  <Button
                    variant="permanentLink"
                    className="transition-colors text-green-primary hover:border-green-primary"
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    Iniciar sesión
                  </Button>
                </Link>
              </>
            )}
          </nav>

          {/* Menú móvil */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Menú</span>
              </Button>
            </SheetTrigger>

            <SheetContent
              aria-describedby={undefined}
              open
              setOpen={setOpen}
              title="Menú de navegación"
              side="right"
            >
              <MobileNav user={user} />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
